import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { AsyncSingleSelectPopover } from "./component/AsyncSingleSelectPopover";
import { useAsyncSelectOptions } from "./hooks/useAsyncSelectOptions";
import { BasePaginatedSelectProps } from "./interface/paginatedSelect.interface";

const DEFAULT_ASYNC_SINGLE_SELECT_PLACEHOLDER = "Select...";
const DEFAULT_ASYNC_SINGLE_SELECT_LIMIT = 10;
const DEFAULT_ASYNC_SINGLE_SELECT_LOAD_MORE_THRESHOLD = 100;

export const AsyncSingleSelect = <T, P = void>(
  props: BasePaginatedSelectProps<T, P>,
): JSX.Element => {
  const {
    useQuery,
    baseParams,
    filterConfig,
    preload,
    filterFn,
    filterQueryList,
    renderConfig,
    defaultLabel,
    notFound,
    loadingSkeleton,
    label,
    placeholder = DEFAULT_ASYNC_SINGLE_SELECT_PLACEHOLDER,
    value,
    onChange,
    disabled = false,
    className,
    triggerClassName,
    noResultsMessage,
    limit = DEFAULT_ASYNC_SINGLE_SELECT_LIMIT,
    loadMoreThreshold = DEFAULT_ASYNC_SINGLE_SELECT_LOAD_MORE_THRESHOLD,
    side,
    defaultSearchValues,
    onOpenChange,
    open: controlledOpen = false,
    isReadOnly = false,
    showArrow = true,
    cacheKey,
    customIcon,
    clearableConfig = { isClearable: true },
    showCheckIcon = true,
    addButton,
    deleteAction,
    portal = false,
  } = props;

  const normalizedValue = value ?? "";
  const [open, setOpen] = useState(controlledOpen);
  const [selectedOption, setSelectedOption] = useState<T | null>(null);
  const prevCacheKeyRef = useRef<string | undefined>(cacheKey);
  const triggerRef = useRef<HTMLButtonElement | HTMLDivElement>(null);
  const hasScrolledToSelectedRef = useRef<boolean>(false);

  const getOptionValue = useMemo(() => {
    return (option: T): string => {
      const optionValue = option[renderConfig.valueKey];

      return String(optionValue || "");
    };
  }, [renderConfig.valueKey]);

  const shouldTreatOptionAsChanged = useCallback(
    (current: T, previous: T | undefined): boolean => {
      if (!previous) {
        return true;
      }

      const { valueKey, labelKey } = renderConfig;

      if (current[valueKey] !== previous[valueKey]) {
        return true;
      }

      if (labelKey !== undefined && current[labelKey] !== previous[labelKey]) {
        return true;
      }

      return false;
    },
    [renderConfig.valueKey, renderConfig.labelKey],
  );

  const {
    allOptions,
    displayOptions,
    error,
    handleScroll,
    hasMore,
    isFetching,
    isLoading,
    loadMore,
    loadingStates,
    scrollContainerRef,
    searchInputRef,
    searchTerm,
    selectedOptionsCache,
    setAllOptions,
    setDisplayOptions,
    setSearchTerm,
    setSelectedOptionsCache,
  } = useAsyncSelectOptions({
    baseParams,
    filterConfig,
    filterFn,
    filterQueryList,
    getOptionValue,
    initialSearchTerm: defaultSearchValues || "",
    limit,
    loadMoreThreshold,
    open,
    preload,
    shouldCacheOption: ({ optionValue, previousCache }) => {
      return optionValue === normalizedValue || previousCache.has(optionValue);
    },
    shouldTreatOptionAsChanged,
    useQuery,
  });

  const renderOption = useCallback(
    (option: T): React.ReactNode => {
      if (renderConfig.labelRenderer) {
        return renderConfig.labelRenderer(option);
      }

      if (renderConfig.labelKey) {
        const label = option[renderConfig.labelKey];

        return (
          <div className='line-clamp-4' title={String(label || "")}>
            {String(label || "")}
          </div>
        );
      }

      const value = option[renderConfig.valueKey];

      return (
        <div className='line-clamp-4' title={String(value || "")}>
          {String(value || "")}
        </div>
      );
    },
    [renderConfig],
  );

  const getIsDisabled = useCallback(
    (option: T): boolean => {
      if (!renderConfig.isDisabled) {
        return false;
      }
      try {
        return renderConfig.isDisabled(option);
      } catch (_error) {
        return false;
      }
    },
    [renderConfig],
  );

  const getDisplayValue = useCallback(
    (option: T): string => {
      if (renderConfig.labelRenderer) {
        return String(renderConfig.labelRenderer(option) || "");
      }

      const labelKey = renderConfig.labelKey || renderConfig.valueKey;

      return String(option[labelKey] || "");
    },
    [renderConfig],
  );

  useEffect(() => {
    if (cacheKey !== prevCacheKeyRef.current) {
      setSelectedOptionsCache(new Map());
      setSelectedOption(null);
      prevCacheKeyRef.current = cacheKey;
    }
  }, [cacheKey, setSelectedOptionsCache]);

  // Auto-fetch next page when filtered list is below minimum (e.g. filterQueryList excluded many)
  const shouldAutoLoadMore =
    open &&
    !isFetching &&
    !isLoading &&
    displayOptions.length < limit &&
    hasMore &&
    !!filterQueryList &&
    allOptions.length > 0;

  useEffect(() => {
    if (!shouldAutoLoadMore) {
      return;
    }
    loadMore();
  }, [shouldAutoLoadMore, loadMore]);

  // Function to scroll to bottom - can be called after adding new option
  const scrollToBottom = useCallback(() => {
    if (scrollContainerRef.current) {
      setTimeout(() => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTop =
            scrollContainerRef.current.scrollHeight;
        }
      }, 150);
    }
  }, [scrollContainerRef]);

  useEffect(() => {
    if (value) {
      // First try to find in display options
      let option = displayOptions.find((opt) => getOptionValue(opt) === value);

      // If not found, try to find in cache
      if (!option && selectedOptionsCache.has(value)) {
        option = selectedOptionsCache.get(value) || undefined;
      }

      setSelectedOption(option || null);
    } else {
      setSelectedOption(null);
    }
  }, [value, displayOptions, getOptionValue, selectedOptionsCache]);

  useEffect(() => {
    if (controlledOpen !== undefined) {
      setOpen(controlledOpen);
    }
    if (controlledOpen && defaultSearchValues) {
      setSearchTerm(defaultSearchValues);
    }
  }, [controlledOpen, defaultSearchValues, setSearchTerm]);

  // Reset scroll tracking when dropdown closes
  useEffect(() => {
    if (!open) {
      hasScrolledToSelectedRef.current = false;
    }
  }, [open]);

  useEffect(() => {
    if (
      !open ||
      !value ||
      displayOptions.length === 0 ||
      hasScrolledToSelectedRef.current ||
      isLoading ||
      isFetching
    ) {
      return;
    }

    const selectedIndex = displayOptions.findIndex(
      (opt) => getOptionValue(opt) === value,
    );

    if (selectedIndex <= 0) {
      hasScrolledToSelectedRef.current = true;

      return;
    }

    hasScrolledToSelectedRef.current = true;

    const scrollToSelected = (): void => {
      requestAnimationFrame(() => {
        setTimeout(() => {
          const container = scrollContainerRef.current;
          if (!container) {
            return;
          }

          const items = container.querySelectorAll("[data-value]");
          const selectedItem = items[selectedIndex] as HTMLElement | undefined;

          if (selectedItem) {
            selectedItem.scrollIntoView({
              block: "center",
              behavior: "smooth",
            });

            return;
          }

          const fallbackItem = container.querySelector(
            `[data-value="${CSS.escape(value)}"]`,
          );
          if (fallbackItem instanceof HTMLElement) {
            fallbackItem.scrollIntoView({
              block: "center",
              behavior: "smooth",
            });
          }
        }, 100);
      });
    };

    scrollToSelected();
  }, [
    open,
    value,
    displayOptions,
    getOptionValue,
    isLoading,
    isFetching,
    scrollContainerRef,
  ]);

  const handleSelect = useCallback(
    (newValue: string) => {
      // Find and cache the selected option
      const option = displayOptions.find(
        (opt) => getOptionValue(opt) === newValue,
      );

      if (option && getIsDisabled(option)) {
        return;
      }

      if (option && newValue) {
        const newCache = new Map(selectedOptionsCache);
        newCache.set(newValue, option);
        setSelectedOptionsCache(newCache);
      }

      const selectedLabel = option ? getDisplayValue(option) : "";

      setSelectedOption(option || null);
      onChange(newValue, option || undefined, selectedLabel);
      setOpen(false);
      setSearchTerm("");
    },
    [
      onChange,
      displayOptions,
      getOptionValue,
      getDisplayValue,
      selectedOptionsCache,
      setSearchTerm,
      setSelectedOptionsCache,
    ],
  );

  const [deletingOptions, setDeletingOptions] = useState<Set<string>>(
    new Set(),
  );

  const handleDeleteOption = useCallback(
    async (
      optionValue: string,
      deleteActionFn?: (id: string) => Promise<void>,
    ) => {
      // If no delete action, just update local state
      if (!deleteActionFn) {
        setAllOptions((prev) =>
          prev.filter((option) => getOptionValue(option) !== optionValue),
        );
        setDisplayOptions((prev) =>
          prev.filter((option) => getOptionValue(option) !== optionValue),
        );
        setSelectedOptionsCache((prevCache) => {
          const newCache = new Map(prevCache);
          newCache.delete(optionValue);

          return newCache;
        });
        if (value === optionValue) {
          setSelectedOption(null);
          onChange("", undefined);
        }

        return;
      }

      // Mark option as being deleted (for loading state)
      setDeletingOptions((prev) => new Set(prev).add(optionValue));

      try {
        // Wait for the delete action to complete (pessimistic approach)
        await deleteActionFn(optionValue);

        // Only update local state AFTER successful deletion
        setAllOptions((prev) =>
          prev.filter((option) => getOptionValue(option) !== optionValue),
        );
        setDisplayOptions((prev) =>
          prev.filter((option) => getOptionValue(option) !== optionValue),
        );
        setSelectedOptionsCache((prevCache) => {
          const newCache = new Map(prevCache);
          newCache.delete(optionValue);

          return newCache;
        });

        // Only clear form value AFTER successful deletion
        if (value === optionValue) {
          setSelectedOption(null);
          onChange("", undefined);
        }
      } catch (_error) {
        // Delete failed - don't update anything
        // The cache-utils will have already shown an error toast
      } finally {
        // Remove from deleting state
        setDeletingOptions((prev) => {
          const newSet = new Set(prev);
          newSet.delete(optionValue);

          return newSet;
        });
      }
    },
    [
      getOptionValue,
      onChange,
      setAllOptions,
      setDisplayOptions,
      setSelectedOptionsCache,
      value,
    ],
  );

  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      setOpen(newOpen);
      if (!newOpen) {
        setSearchTerm("");
      }
      if (onOpenChange) {
        onOpenChange(newOpen);
      }
    },
    [onOpenChange, setSearchTerm],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (disabled || isReadOnly) {
        return;
      }

      if (e.key === "Tab" && !open) {
        e.preventDefault();
        handleOpenChange(true);
      } else if (e.key === "Escape" && open) {
        e.preventDefault();
        e.stopPropagation();
        handleOpenChange(false);
      }
    },
    [disabled, isReadOnly, open, handleOpenChange],
  );

  const triggerDisplayValue = useMemo(() => {
    if (selectedOption) {
      return getDisplayValue(selectedOption);
    }

    if (value) {
      const cachedOption = selectedOptionsCache.get(value);
      if (cachedOption) {
        return getDisplayValue(cachedOption);
      }

      return defaultLabel || value;
    }

    return placeholder;
  }, [
    selectedOption,
    value,
    defaultLabel,
    getDisplayValue,
    placeholder,
    selectedOptionsCache,
  ]);

  useEffect((): (() => void) | undefined => {
    if (!open || loadingStates.showMainSkeleton) {
      return undefined;
    }

    const timer = setTimeout((): void => {
      searchInputRef.current?.focus();
    }, 0);

    return (): void => clearTimeout(timer);
  }, [open, loadingStates.showMainSkeleton, searchInputRef]);

  const hasValidTriggerValue =
    selectedOption ||
    (value && selectedOptionsCache.has(value)) ||
    (value && defaultLabel);

  const popoverTriggerProps = useMemo(
    () => ({
      className,
      clearableConfig,
      customIcon,
      disabled,
      hasValidTriggerValue: Boolean(hasValidTriggerValue),
      isReadOnly,
      onKeyDown: handleKeyDown,
      onOpenChange: handleOpenChange,
      open,
      showArrow,
      side,
      triggerClassName,
      triggerDisplayValue,
      triggerRef,
      value,
      portal,
    }),
    [
      className,
      clearableConfig,
      customIcon,
      disabled,
      handleKeyDown,
      handleOpenChange,
      hasValidTriggerValue,
      isReadOnly,
      open,
      showArrow,
      side,
      triggerClassName,
      triggerDisplayValue,
      value,
      portal,
    ],
  );

  const popoverSearchProps = useMemo(
    () => ({
      label,
      loadingSkeleton,
      loadingState: loadingStates,
      searchInputRef,
      searchTerm,
      setSearchTerm,
    }),
    [
      label,
      loadingSkeleton,
      loadingStates,
      searchInputRef,
      searchTerm,
      setSearchTerm,
    ],
  );

  const popoverListProps = useMemo(
    () => ({
      allOptions,
      deleteAction,
      deletingOptions,
      displayOptions,
      hasError: Boolean(error),
      filterQueryList,
      getOptionValue,
      handleDeleteOption,
      handleScroll,
      handleSelect,
      isFetching,
      isLoading,
      noResultsMessage,
      notFound,
      renderCustomValue: renderConfig.customRender,
      renderOption,
      scrollContainerRef,
      showCheckIcon,
      value,
      getIsDisabled,
    }),
    [
      allOptions,
      deleteAction,
      deletingOptions,
      displayOptions,
      error,
      filterQueryList,
      getOptionValue,
      handleDeleteOption,
      handleScroll,
      handleSelect,
      isFetching,
      isLoading,
      noResultsMessage,
      notFound,
      renderConfig.customRender,
      renderOption,
      scrollContainerRef,
      showCheckIcon,
      value,
      getIsDisabled,
    ],
  );

  const popoverFooterProps = useMemo(
    () => ({
      addButton,
      onClearSelection: (): void => {
        if (clearableConfig.onClear) {
          clearableConfig.onClear();

          return;
        }

        handleSelect("");
      },
      scrollToBottom,
    }),
    [addButton, clearableConfig, handleSelect, scrollToBottom],
  );

  return (
    <AsyncSingleSelectPopover
      footerProps={popoverFooterProps}
      listProps={popoverListProps}
      searchProps={popoverSearchProps}
      triggerProps={popoverTriggerProps}
    />
  );
};
